/* Site-wide delivery zip checker popup.

   Opened by any `a[href="#zip-code-checker"]` link anywhere on the storefront (homepage FAQ,
   FAQ page, admin nav, etc.). Validates a 5-digit zip against window.deliveryZones — the
   storefront-readable stocked.delivery_zones metafield injected globally in theme.liquid.

   Shares the `cartValidZipCode` localStorage key + the `zipcode:validated` event with the
   cart's free-shipping progress bar (component-free-shipping-progress.js), so a zip checked
   here keeps that bar in sync. The open/close pattern mirrors <cart-delivery-date data-mode
   ="modal"> in delivery-date.js (hidden + .is-active transition, body scroll lock, focus trap). */
(() => {
  const TRIGGER_SELECTOR = 'a[href="#zip-code-checker"]';

  class ZipCheckerModal extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.cart-zip-validator__input');
      this.button = this.querySelector('.cart-zip-validator__button');
      this.message = this.querySelector('.cart-zip-validator__message');
      this.waitlist = this.querySelector('.zip-checker-modal__waitlist');
      if (!this.input || !this.button || !this.message) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onTriggerClick = this.onTriggerClick.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('input', this.onInput);
      // Delegated on document so every #zip-code-checker link opens this popup — including
      // any added later in admin nav/rich text. Replaces the old cart-drawer handler.
      document.addEventListener('click', this.onTriggerClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.input) this.input.removeEventListener('input', this.onInput);
      document.removeEventListener('click', this.onTriggerClick);
    }

    /* --------------------------------------------------------------- trigger */

    onTriggerClick(event) {
      const link = event.target.closest(TRIGGER_SELECTOR);
      if (!link) return;
      event.preventDefault();
      this.open(link);
    }

    /* ------------------------------------------------------------- modal API */

    open(opener) {
      this.opener = opener || null;
      this.prefillFromStorage();
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      // Add .is-active next frame so the overlay/dialog transition plays.
      requestAnimationFrame(() => this.classList.add('is-active'));
      trapFocus(this, this.input);
    }

    close() {
      this.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.opener);
      this.opener = null;
      // Let the fade/scale-out transition play before hiding.
      const hide = () => {
        this.hidden = true;
        this.removeEventListener('transitionend', hide);
      };
      this.addEventListener('transitionend', hide, { once: true });
      setTimeout(hide, 400); // fallback if transitionend doesn't fire
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      if (event.target.closest('[data-zip-close], [data-zip-overlay]')) {
        this.close();
        return;
      }
      if (event.target.closest('.cart-zip-validator__button')) {
        this.validate();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Enter' && event.target === this.input) {
        event.preventDefault();
        this.validate();
      }
    }

    onInput() {
      const cleaned = this.input.value.replace(/\D/g, '').slice(0, 5);
      if (this.input.value !== cleaned) this.input.value = cleaned;
      // Reset feedback while the customer is editing.
      this.clearMessage();
      this.input.classList.remove('error');
      this.hideWaitlist();
    }

    /* ------------------------------------------------------------ validation */

    validate() {
      const zip = this.input.value.trim();

      if (!zip) {
        this.showError('Please enter a zip code.');
        this.input.focus();
        return;
      }
      if (zip.length !== 5) {
        this.showError('Please enter a valid 5-digit zip code.');
        this.input.focus();
        return;
      }
      if (!Array.isArray(window.deliveryZones)) {
        console.error('Delivery zones data is not available');
        this.showError('Unable to verify zip code. Please try again.');
        return;
      }

      if (window.StockedDeliveryZones.isServiceable(zip)) {
        window.StockedDeliveryZones.saveZip(zip);
        this.showSuccess('Stocked is available in your area.');
        this.hideWaitlist();
      } else {
        this.showError('Stocked is not available in your area.');
        this.showWaitlist();
      }
    }

    prefillFromStorage() {
      const saved = window.StockedDeliveryZones.getSavedZip();
      // Start clean each open; surface the last zip but make the customer re-check it.
      this.clearMessage();
      this.input.classList.remove('error');
      this.hideWaitlist();
      this.input.value = saved || '';
    }

    /* -------------------------------------------------------------- messages */

    showSuccess(text) {
      this.message.textContent = text;
      this.message.className = 'cart-zip-validator__message success';
      this.input.classList.remove('error');
    }

    showError(text) {
      this.message.textContent = text;
      this.message.className = 'cart-zip-validator__message error';
      this.input.classList.add('error');
    }

    clearMessage() {
      this.message.textContent = '';
      this.message.className = 'cart-zip-validator__message';
    }

    showWaitlist() {
      if (this.waitlist) this.waitlist.hidden = false;
    }

    hideWaitlist() {
      if (this.waitlist) this.waitlist.hidden = true;
    }
  }

  if (!customElements.get('zip-checker-modal')) {
    customElements.define('zip-checker-modal', ZipCheckerModal);
  }
})();
